import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { WaveDetailHook } from '../../../../hooks/waves';
import { useAppHeaderLead } from '../../../../hooks/useAppHeaderLead';
import { useHeaderActions } from '../../../../hooks/useHeaderActions';
import {
  AssignAllocationPopup,
  AssignWaveToUserPopup,
  ChangeLocationPopup,
  TotePopup,
  WaveDetailHeaderActions,
  WaveDetailHeaderLead,
} from '../../../../page-components/waves';
import { WaveDetailAllocationsTable } from '../../../../page-components/waves/WaveDetailAllocationsTable';
import { WaveHistory } from '../../../../page-components/waves/WaveHistory';
import { WaveCancellationAlertsBanner } from '../../../../page-components/waves/WaveCancellationAlertsBanner';
import { WaveDetailAllocationSelectionFooter } from '../../../../page-components/waves/WaveDetailAllocationSelectionFooter';
import { CancelAllocationsModal } from '../../../../page-components/waves/popup/CancelAllocationsModal';
import {
  flattenWaveAllocations,
  getAllocationsNeedingCancellation,
} from '../../../../page-components/waves/cancellationAlerts';
import {
  AllocationService,
  CommonUtility,
  DateFormat,
  DateUtility,
  ToastMessage,
  WaveService,
} from '../../../../utility';
import { ConfirmationPopup } from '../../../../components';
import { MobileApplicationBanner } from '../../../../page-components/waves/common';
import type { GroupedWaveAllocation, WaveAllocation } from 'types/wave/detail';

const cancellationTypes = {
  cancel: 'cancel',
  remove: 'remove',
};

export function DesktopWaveDetailScreen() {
  const { id } = useParams();

  const { data, loading, refresh, setLoading, allocations, exportCSV, exporting } =
    WaveDetailHook(id);
  const [userPopup, setUserPopup] = useState(false);
  const [totePopupItem, setTotePopupItem] = useState(null);

  const [deleting, setDeleting] = useState(false);
  const [deleteWavePopup, setDeleteWavePopup] = useState(false);

  const navigate = useNavigate();

  const [itemMarkedTracker, setItemMarkedTracker] = useState({});
  const [confirmationPopup, setConfirmationPopup] = useState({
    open: false,
    item: null,
    type: null,
  });
  const [changeLocationPopup, setChangeLocationPopup] = useState({
    item: null,
    type: null,
    open: false,
  });

  const [processing, setProcessing] = useState(false);
  const [documentGenerating, setDocumentGenerating] = useState(false);
  const [emailGenerating, setEmailGenerating] = useState(false);
  const [allocation, setAllocation] = useState<GroupedWaveAllocation | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelModalAllocations, setCancelModalAllocations] = useState<WaveAllocation[]>(
    [],
  );
  const [cancelModalPreselectedIds, setCancelModalPreselectedIds] = useState<string[]>(
    [],
  );
  const [cancelModalDescription, setCancelModalDescription] = useState('');
  const [selectedAllocationIds, setSelectedAllocationIds] = useState<string[]>([]);

  const flattenedAllocations = useMemo(
    () => flattenWaveAllocations(allocations),
    [allocations],
  );

  const cancellationAllocations = useMemo(
    () => getAllocationsNeedingCancellation(data?.alerts, allocations),
    [data?.alerts, allocations],
  );

  const cancellationAllocationIds = useMemo(
    () => new Set(cancellationAllocations.map(allocation => allocation.id)),
    [cancellationAllocations],
  );

  const openCancelModal = (
    modalAllocations: WaveAllocation[],
    preselectedIds: string[],
    description: string,
  ) => {
    setCancelModalAllocations(modalAllocations);
    setCancelModalPreselectedIds(preselectedIds);
    setCancelModalDescription(description);
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setCancelModalOpen(false);
  };

  const handleCancelModalSuccess = () => {
    setSelectedAllocationIds([]);
    refresh();
  };

  const openAlertCancelModal = () => {
    openCancelModal(
      cancellationAllocations,
      cancellationAllocations.map(allocation => allocation.id),
      'These allocations belong to cancelled orders and should be removed from the wave.',
    );
  };

  const openSelectedCancelModal = () => {
    const selectedAllocations = flattenedAllocations.filter(allocation =>
      selectedAllocationIds.includes(allocation.id),
    );

    openCancelModal(
      selectedAllocations,
      selectedAllocationIds,
      'Review the selected allocations before cancelling them from the wave.',
    );
  };

  useEffect(() => {
    if (CommonUtility.isValidObject(data?.wave)) {
      const currentStatus = {};
      data?.wave?.allocations?.forEach(allocation => {
        currentStatus[allocation?.id] = allocation.status;
      });

      setItemMarkedTracker(currentStatus);
    }
  }, [data]);

  const setItem = (itemId, value) => {
    setItemMarkedTracker(itemMarkedTracker => ({
      ...itemMarkedTracker,
      [itemId]: value,
    }));
  };

  const checkIfAllItemsHaveBeenPicked = statusTracker => {
    const statusArray = Object.values(statusTracker || {});
    const everyItemIsPicked = statusArray.every(x => x === statusArray[0]);
    if (everyItemIsPicked) {
      refresh();
    }
  };

  const markWaveReady = async () => {
    try {
      setLoading(true);
      await WaveService.markWaveReady(id);
      ToastMessage.success('Wave marked as ready successfully');
      refresh();
    } catch (error) {
      setLoading(false);
      ToastMessage.apiError(error);
    }
  };

  const moveWaveToBuilding = async () => {
    try {
      setLoading(true);
      await WaveService.moveToBuilding(id);
      ToastMessage.success('Moved wave to building state successfully');
      refresh();
    } catch (error) {
      setLoading(false);
      ToastMessage.apiError(error);
    }
  };

  const sendItemForAllocation = async (item: GroupedWaveAllocation, toteId?: string) => {
    const previousStatus = item.status;
    try {
      setItem(item.id, 'loading');

      const payload: { toteId?: string } = {};
      if (item.sorting?.requiresTote) {
        payload.toteId = toteId;
      }

      const res = await WaveService.sortSingleItem(id, item.id, payload);
      setItem(item.id, res?.data?.status);
      checkIfAllItemsHaveBeenPicked({
        ...itemMarkedTracker,
        [item.id]: res?.data?.status,
      });
      ToastMessage.success('Item marked as picked successfully');
    } catch (error) {
      setItem(item.id, previousStatus);
      ToastMessage.apiError(error);
    }
  };

  const markItemAsAllocated = item => {
    if (item.sorting?.requiresTote) {
      setTotePopupItem(item);
      return;
    }

    sendItemForAllocation(item);
  };

  const closeTotePopup = () => {
    setTotePopupItem(null);
  };

  const removeAllocation = item => {
    setConfirmationPopup({
      open: true,
      type: cancellationTypes.remove,
      item,
    });
  };

  const changeAllocation = item => {
    setChangeLocationPopup({
      open: true,
      item,
      type: null,
    });
  };

  const cancelAllocation = item => {
    setConfirmationPopup({
      open: true,
      type: cancellationTypes.cancel,
      item,
    });
  };

  const onCloseConfirmation = () => {
    setConfirmationPopup({
      open: false,
      item: null,
      type: null,
    });
  };

  const onConfirm = async () => {
    try {
      setProcessing(true);
      if (confirmationPopup.type === cancellationTypes.cancel) {
        await AllocationService.cancelAllocation(confirmationPopup.item.id);
        ToastMessage.success('Allocation cancelled successfully');
      } else {
        await AllocationService.removeAllocation(id, confirmationPopup.item.id);
        ToastMessage.success('Allocation removed successfully');
      }
      onCloseConfirmation();
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  const openAssignUserPopup = () => {
    setUserPopup(true);
  };

  const closeAssignUserPopup = () => {
    setUserPopup(false);
  };

  const generateEmail = async () => {
    try {
      setEmailGenerating(true);
      await WaveService.generateMail(id);
      ToastMessage.success('The labels have been sent to your email.');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setEmailGenerating(false);
    }
  };

  const generateDocument = async () => {
    try {
      setDocumentGenerating(true);
      const res = await WaveService.generateDocument(
        id,
        { responseType: 'blob' },
        { 'Content-Type': 'application/pdf' },
      );

      const date = DateUtility.formatDate(new Date(), DateFormat.dateTime);
      const fileName = `wave-${data?.wave?.waveNumber}-${date}.pdf`;
      CommonUtility.downloadPDF(res, fileName);
      ToastMessage.success('Document generated successfully');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setDocumentGenerating(false);
    }
  };

  const assignAllocation = allocation => {
    setAllocation(allocation);
  };

  const onAssignItem = async waveId => {
    try {
      setProcessing(true);
      const payload = {
        previousWaveId: id,
      };
      await AllocationService.assignAllocationToWave(waveId, allocation.id, payload);
      onClose();
      ToastMessage.success('Item moved to antoher wave successfully');
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  const onClose = () => {
    setAllocation(null);
  };

  const deleteWave = async () => {
    try {
      setDeleting(true);
      await WaveService.deleteWave(id);
      ToastMessage.success('Wave deleted successfully');
      navigate('/app/fulfill/waves/list/building');
      setDeleteWavePopup(false);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteWave = () => {
    if (allocations.length === 0) {
      setDeleteWavePopup(true);
    } else {
      ToastMessage.error('Cannot delete wave with allocations');
    }
  };

  const wave = data?.wave;
  const isWaveInBuilding = wave?.status === 'building';
  const isWaveInReady = wave?.status === 'ready';
  const canSendEmail = ['sorted', 'completed'].includes(wave?.status);

  useAppHeaderLead(
    () => <WaveDetailHeaderLead data={wave} summary={data?.summary} loading={loading} />,
    [wave, data?.summary, loading],
  );

  useHeaderActions(
    () => (
      <WaveDetailHeaderActions
        loading={loading}
        isWaveInBuilding={isWaveInBuilding}
        isWaveInReady={isWaveInReady}
        markWaveReady={markWaveReady}
        moveWaveToBuilding={moveWaveToBuilding}
        assignToUser={openAssignUserPopup}
        deleteWave={openDeleteWave}
        canSendEmail={canSendEmail}
        generateEmail={generateEmail}
        emailGenerating={emailGenerating}
        generateDocument={generateDocument}
        documentGenerating={documentGenerating}
        exportCSV={exportCSV}
        exporting={exporting}
      />
    ),
    [
      loading,
      isWaveInBuilding,
      isWaveInReady,
      canSendEmail,
      markWaveReady,
      moveWaveToBuilding,
      openAssignUserPopup,
      openDeleteWave,
      generateEmail,
      emailGenerating,
      generateDocument,
      documentGenerating,
      exportCSV,
      exporting,
    ],
  );

  return (
    <div className={selectedAllocationIds.length > 0 ? 'pb-4 mb-4' : ''}>
      <MobileApplicationBanner />
      {wave ? (
        <div className="mb-3">
          <WaveHistory wave={wave} />
        </div>
      ) : null}
      <WaveCancellationAlertsBanner
        alerts={data?.alerts || []}
        count={cancellationAllocations.length}
        onReview={openAlertCancelModal}
      />
      <WaveDetailAllocationsTable
        allocations={allocations}
        loading={loading}
        itemMarkedTracker={itemMarkedTracker}
        isWaveInBuilding={data?.wave?.status === 'building'}
        cancellationAllocationIds={cancellationAllocationIds}
        selectedAllocationIds={selectedAllocationIds}
        onSelectionChange={setSelectedAllocationIds}
        removeAllocation={removeAllocation}
        cancelAllocation={cancelAllocation}
        changeAllocation={changeAllocation}
        assignAllocation={assignAllocation}
      />
      <TotePopup
        item={totePopupItem}
        open={!!totePopupItem}
        onClose={closeTotePopup}
        warehouse={data?.wave?.warehouseId}
        afterToteCreation={sendItemForAllocation}
      />
      <AssignWaveToUserPopup
        waveId={id}
        open={userPopup}
        onClose={closeAssignUserPopup}
        refresh={refresh}
      />
      <ChangeLocationPopup
        item={changeLocationPopup.item}
        open={changeLocationPopup.open}
        warehouse={data?.wave?.warehouseId}
        refresh={refresh}
        onClose={() => setChangeLocationPopup({ open: false, item: null, type: null })}
      />
      <ConfirmationPopup
        open={confirmationPopup.open}
        title={`${
          confirmationPopup.type === cancellationTypes.cancel ? 'Cancel' : 'Remove'
        } ${confirmationPopup?.item?.inventory?.product.sku} Allocation`}
        message={
          confirmationPopup.type === cancellationTypes.cancel
            ? 'This will cancel the allocation and remove it completely from WMS.'
            : 'This will remove the allocation but keep the item'
        }
        onClose={onCloseConfirmation}
        onConfirm={onConfirm}
        loading={processing}
        closeText="Close"
        confirmText="Remove"
      />
      <AssignAllocationPopup
        open={allocation}
        onClose={onClose}
        warehouseId={data?.wave?.warehouseId?._id}
        allocation={allocation}
        onAssignItem={onAssignItem}
        processing={processing}
        waveId={id}
      />
      <ConfirmationPopup
        open={deleteWavePopup}
        title="Delete Wave"
        message="Are you sure you want to delete this wave"
        closeText="Close"
        confirmText="Delete"
        onClose={() => setDeleteWavePopup(false)}
        onConfirm={deleteWave}
        loading={deleting}
      />
      <CancelAllocationsModal
        open={cancelModalOpen}
        onClose={closeCancelModal}
        allocations={cancelModalAllocations}
        preselectedIds={cancelModalPreselectedIds}
        description={cancelModalDescription}
        onSuccess={handleCancelModalSuccess}
      />
      <WaveDetailAllocationSelectionFooter
        selectedCount={selectedAllocationIds.length}
        onClear={() => setSelectedAllocationIds([])}
        onCancelAllocations={openSelectedCancelModal}
      />
    </div>
  );
}
